/**
 * Hotel Booking Review Thunk
 *
 * Fetches hotel booking review details using MCP format
 * POST /hotels/booking-review
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import type { HotelReviewTemplateData, HotelBookingReviewMCPRequest } from '@contracts';
import { fetchHotelBookingReview as fetchHotelBookingReviewApi, HotelReviewError } from '../../api';
import type { RootState, AppDispatch } from '@/store/store';
import { setActiveTemplate } from '@/features/chat/redux';
import { TEMPLATE_TYPES } from '@/features/chat/constants/templateTypes';
import { logger } from '@/shared/utils/logger';
import { getOrCreateSessionId } from '@/shared/utils';
import { getUUID } from '@/shared/utils/appCookies';
import { TRACKING_DATA, fireTrackingEvents } from '@shared/analytics';

// ============================================
// Types
// ============================================

interface FetchHotelBookingReviewParams {
  /** MCP format payload (built by caller) */
  payload: HotelBookingReviewMCPRequest;
}

interface FetchHotelBookingReviewResult {
  templateData: HotelReviewTemplateData;
}

// ============================================
// Thunk
// ============================================

/**
 * Fetch hotel booking review details using MCP format
 * POST /hotels/booking-review
 */
interface ReviewErrorPayload {
  message: string;
  code?: string | undefined;
}

export const fetchHotelBookingReviewThunk = createAsyncThunk<
  FetchHotelBookingReviewResult,
  FetchHotelBookingReviewParams,
  { state: RootState; dispatch: AppDispatch; rejectValue: ReviewErrorPayload }
>(
  'hotels/fetchBookingReview',
  async (params, { dispatch, rejectWithValue }) => {
    try {
      const sessionId = getOrCreateSessionId();
      const reviewTemplate = await fetchHotelBookingReviewApi({
        sessionId,
        userId: getUUID(),
        payload: params.payload,
      });

      // Handle API error response
      if (!reviewTemplate.success || !reviewTemplate.data) {
        const errorMessage =
          reviewTemplate.error?.message ?? 'Failed to fetch hotel booking review';
        const errorCode = reviewTemplate.error?.code;
        logger.error('[BookingReviewThunk] API returned error:', reviewTemplate.error);
        return rejectWithValue({ message: errorMessage, code: errorCode });
      }

      const trackingInfo = TRACKING_DATA[TEMPLATE_TYPES.HOTEL_REVIEW] ?? null;
      dispatch(
        setActiveTemplate({
          template: 'hotels-review',
          data: reviewTemplate.data,
          trackingInfo,
        }),
      );
      fireTrackingEvents(trackingInfo);

      return {
        templateData: reviewTemplate.data,
      };
    } catch (error) {
      logger.error('[BookingReviewThunk] Failed to fetch hotel booking review:', error);
      if (error instanceof HotelReviewError) {
        return rejectWithValue({ message: error.message });
      }
      return rejectWithValue({ message: 'Failed to fetch hotel booking review' });
    }
  },
  {
    // Prevent duplicate API calls - condition runs BEFORE pending action is dispatched
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      const { isReviewLoading } = state.hotels.bookingReview;
      // Return false to cancel the thunk if already loading
      return !isReviewLoading;
    },
  },
);
